import React from 'react'
import { cn } from '@/lib/utils'
import { CheckIcon, CircleDotIcon, CircleIcon, PauseCircleIcon } from 'lucide-react'

export interface Step {
  id: string
  label: string
  description?: string
}

export interface StepProgressProps {
  steps: Step[]
  currentStepId: string
  className?: string
  statusColors?: Record<string, string>
}

export function StepProgress({ steps, currentStepId, className, statusColors = {} }: StepProgressProps) {
  // Find the index of the current step
  const currentStepIndex = steps.findIndex(step => step.id === currentStepId)
  
  // Default color for the current step if no specific color is provided
  const defaultCurrentColor = "text-blue-600"
  
  // Get the color for the current status if available
  const currentStatusColor = statusColors[currentStepId] || defaultCurrentColor
  
  // Function to get the appropriate icon for a step
  const getStepIcon = (stepId: string, isCompleted: boolean, isCurrent: boolean) => {
    if (isCompleted) {
      return <CheckIcon className="h-5 w-5" />
    }
    
    if (isCurrent) {
      if (stepId === 'on hold') {
        return <PauseCircleIcon className="h-5 w-5" />
      }
      return <CircleDotIcon className="h-5 w-5" />
    }
    
    return <CircleIcon className="h-5 w-5" />
  }
  
  return (
    <div className={cn("w-full", className)}>
      <div className="flex items-center justify-center">
        {steps.map((step, index) => {
          // Determine if this step is completed, current, or upcoming
          const isCompleted = index < currentStepIndex
          const isCurrent = index === currentStepIndex || step.id === currentStepId
          const isUpcoming = index > currentStepIndex && step.id !== currentStepId
          
          // Determine icon color class
          let iconColorClass = "text-gray-300" // Default for upcoming steps
          
          if (isCurrent) {
            // For current step, use its status color
            const colorParts = currentStatusColor.split(' ')
            const textColorPart = colorParts.find(part => part.startsWith('text-'))
            iconColorClass = textColorPart || `text-${currentStatusColor.split('-')[1]}-500`
            
            // Special case for on hold (red)
            if (step.id === 'on hold') {
              iconColorClass = 'text-red-500'
            }
          } else if (isCompleted) {
            iconColorClass = "text-gray-400" // Completed steps
          }
          
          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center relative group">
                {/* Step icon with colored background for current step */}
                <div 
                  className={cn(
                    "flex items-center justify-center rounded-full p-1 z-10", // Added z-index to appear above the line
                    isCurrent && step.id === 'on hold' && "bg-red-100",
                    isCurrent && step.id === 'not ready' && "bg-yellow-100",
                    isCurrent && step.id === 'ready for release' && "bg-blue-100",
                    isCurrent && step.id === 'released' && "bg-green-100"
                  )}
                  title={step.description} // Native HTML tooltip
                >
                  <div className={iconColorClass}>
                    {getStepIcon(step.id, isCompleted, isCurrent)}
                  </div>
                </div>
                
                {/* Step label */}
                <p 
                  className={cn(
                    "text-xs font-medium mt-1",
                    isCompleted && "text-gray-500", // Completed steps text is gray
                    isCurrent && "text-gray-900", // Current step text is dark
                    isUpcoming && "text-gray-400" // Upcoming steps text is light gray
                  )}
                >
                  {step.label}
                </p>
                
                {/* CSS tooltip */}
                <div className="absolute bottom-full mb-2 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
                  {step.description}
                </div>
              </div>
              
              {/* Connector line between steps */}
              {index < steps.length - 1 && (
                <div className={cn(
                  "w-16 h-0.5 mx-1", // Line dimensions
                  index < currentStepIndex ? "bg-gray-400" : "bg-gray-200" // Completed lines are darker
                )} />
              )}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}
